import styled, { createGlobalStyle, keyframes } from 'styled-components'

const ringPulse = keyframes`
  0% { transform: scale(1); opacity: .8; }
  50% { transform: scale(1.6); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
`
const homePulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0,229,160,.6); }
  70% { box-shadow: 0 0 0 18px rgba(0,229,160,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,229,160,0); }
`
const spin = keyframes`to { transform: rotate(360deg); }`
const slideUp = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`
const dotPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: .4; }
`

const c = {
  bg: '#0a0c0f', bg2: '#111318', bg3: '#181c23', bg4: '#1e2330',
  bdr: '#1f2430', bdr2: '#2a3040',
  acc: '#00e5a0', acc2: '#00b87a',
  adim: 'rgba(0,229,160,.08)', adim2: 'rgba(0,229,160,.18)',
  txt: '#e8ecf0', txt2: '#8a95a3', txt3: '#5a6370',
  warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6',
  mono: "'Space Mono',monospace", sans: "'DM Sans',sans-serif",
}

// ─── GLOBAL (leaflet overrides + fonts + shared popup classes) ───────────────
export const GlobalMapStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

  .leaflet-popup-content-wrapper {
    background: ${c.bg2} !important; border: 1px solid ${c.bdr2} !important;
    border-radius: 10px !important; box-shadow: 0 8px 32px rgba(0,0,0,.6) !important;
    padding: 0 !important;
  }
  .leaflet-popup-tip         { background: ${c.bg2} !important; }
  .leaflet-popup-content     { margin: 0 !important; width: 220px !important; }
  .leaflet-tooltip {
    background: ${c.bg2} !important; border: 1px solid ${c.bdr2} !important;
    color: ${c.txt} !important; font-size: 10px !important;
    border-radius: 6px !important; padding: 4px 8px !important;
    box-shadow: 0 4px 16px rgba(0,0,0,.5) !important;
  }
  .home-popup .leaflet-popup-content-wrapper { border-color: ${c.acc} !important; }

  @keyframes ringPulse {
    0%   { transform: scale(1);   opacity: .8; }
    50%  { transform: scale(1.6); opacity: 0;  }
    100% { transform: scale(1);   opacity: 0;  }
  }
  @keyframes homePulse {
    0%   { box-shadow: 0 0 0 0   rgba(0,229,160,.6); }
    70%  { box-shadow: 0 0 0 18px rgba(0,229,160,0); }
    100% { box-shadow: 0 0 0 0   rgba(0,229,160,0);  }
  }

  .home-marker {
    width:32px; height:32px; border-radius:50%;
    background: linear-gradient(135deg,#00e5a0,#00b87a);
    border:3px solid #000; display:flex; align-items:center; justify-content:center;
    font-size:16px; box-shadow:0 4px 14px rgba(0,229,160,.5);
    animation: homePulse 2s infinite; cursor:pointer;
  }

  /* Popup inner helpers */
  .pu { padding:12px 14px; }
  .pu-ped { font-family:'Space Mono',monospace; font-size:11px; font-weight:700; color:${c.acc}; margin-bottom:8px; }
  .pu-row { display:flex; justify-content:space-between; font-size:10px; padding:3px 0; border-bottom:1px solid ${c.bdr}; }
  .pu-row:last-of-type { border:none; }
  .pu-row .k { color:${c.txt3}; }
  .pu-row .v { color:${c.txt}; text-align:right; max-width:140px; word-break:break-word; }
  .pu-weight { margin-top:8px; background:${c.bg3}; border-radius:6px; padding:6px 10px; display:flex; align-items:center; justify-content:space-between; }
  .pu-weight .wlbl { font-size:10px; color:${c.txt3}; }
  .pu-weight .wval { font-family:'Space Mono',monospace; font-size:14px; font-weight:700; color:${c.warn}; }
  .pu-transp { margin-top:7px; padding:4px 8px; border-radius:5px; font-size:10px; text-align:center; color:${c.txt3}; background:${c.bg3}; border:1px solid ${c.bdr2}; }
  .pu-home-header { background:linear-gradient(135deg,${c.adim2},transparent); padding:10px 14px; border-bottom:1px solid ${c.bdr}; }
  .pu-home-title  { font-family:'Space Mono',monospace; font-size:12px; font-weight:700; color:${c.acc}; }
  .pu-home-addr   { font-size:11px; color:${c.txt}; margin-top:4px; line-height:1.4; }
`

// ─── LAYOUT ──────────────────────────────────────────────────────────────────
export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  height:100vh; display:flex; flex-direction:column; overflow:hidden;
`

// ─── TABS BAR ─────────────────────────────────────────────────────────────────
export const TabsBar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:0 16px; height:40px;
  display:flex; align-items:center; gap:2px; flex-shrink:0;
`
export const TabBtn = styled.button`
  background:transparent; border:none;
  color:${({ $active }) => $active ? c.acc : c.txt3};
  padding:8px 14px; font-size:12px; font-weight:500; cursor:pointer;
  border-radius:6px 6px 0 0; transition:.15s;
  font-family:${c.sans}; display:flex; align-items:center; gap:6px; position:relative;
  &:hover { color:${c.txt}; background:${c.bg3}; }
  ${({ $active }) => $active && `
    &::after { content:''; position:absolute; left:10px; right:10px; bottom:-1px;
               height:2px; background:${c.acc}; border-radius:2px; }
  `}
`
export const TabBadge = styled.span`
  font-family:${c.mono}; font-size:10px;
  background:${({ $active }) => $active ? c.adim : c.bg3};
  padding:1px 7px; border-radius:10px;
  color:${({ $active }) => $active ? c.acc : c.txt2}; margin-left:2px;
`

// ─── STATUS FILTER (chip bar dentro da TabsBar) ───────────────────────────
export const StatusFilterBar = styled.div`
  margin-left:auto;
  display:flex; align-items:center; gap:6px; flex-wrap:wrap;
`
export const StatusFilterLabel = styled.span`
  font-size:9px; color:${c.txt3};
  text-transform:uppercase; letter-spacing:.06em;
  margin-right:2px; font-family:${c.sans};
`
export const StatusChip = styled.button`
  background:${({ $active }) => $active ? c.adim2 : 'transparent'};
  border:1px solid ${({ $active }) => $active ? c.acc2 : c.bdr2};
  color:${({ $active }) => $active ? c.acc : c.txt2};
  border-radius:14px; padding:4px 11px;
  font-size:11px; font-weight:500; cursor:pointer; transition:.12s;
  display:inline-flex; align-items:center; gap:6px;
  font-family:${c.sans}; line-height:1;
  &:hover { border-color:${c.acc}; color:${c.acc}; }
`
export const StatusChipCount = styled.span`
  font-family:${c.mono}; font-size:10px; font-weight:700;
  opacity:.8;
`
export const StatusClearBtn = styled.button`
  background:transparent; border:none; color:${c.txt3};
  font-size:10px; cursor:pointer; padding:4px 6px;
  font-family:${c.sans}; transition:.12s;
  &:hover { color:${c.danger}; }
`

// ─── MAP SCREEN ───────────────────────────────────────────────────────────────
export const MapScreen = styled.div`
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  flex:1; overflow:hidden;
`
export const MapWrap = styled.div`display:flex; height:100%; width: 100%`

// ─── SIDEBAR ──────────────────────────────────────────────────────────────────
export const Sidebar = styled.div`
  width:310px; flex-shrink:0;
  background:${c.bg2}; border-right:1px solid ${c.bdr};
  display:flex; flex-direction:column; overflow:hidden;
`
export const StatsRow = styled.div`
  display:grid; grid-template-columns:1fr 1fr 1fr;
  gap:5px; padding:10px; border-bottom:1px solid ${c.bdr}; flex-shrink:0;
`
export const StatCard = styled.div`
  background:${c.bg3}; border-radius:7px; padding:8px 9px;
  .v { font-family:${c.mono}; font-size:16px; font-weight:700; color:${c.acc}; }
  .l { font-size:9px; color:${c.txt3}; margin-top:1px; text-transform:uppercase; letter-spacing:.05em; }
`
export const GruposHeader = styled.div`
  padding:10px; border-bottom:1px solid ${c.bdr}; flex-shrink:0;
  p { font-size:11px; color:${c.txt3}; margin-bottom:8px; line-height:1.5; }
`
export const BtnNewGroup = styled.button`
  width:100%; padding:8px;
  background:${c.adim2}; border:1px solid ${c.acc2}; border-radius:8px;
  color:${c.acc}; font-size:12px; font-weight:600; cursor:pointer;
  transition:.15s; font-family:${c.sans};
  &:hover { background:${c.acc}; color:#000; }
  &:disabled { opacity:.35; cursor:not-allowed; }
`
export const GruposList = styled.div`
  overflow-y:auto; flex:1; padding:9px;
  &::-webkit-scrollbar { width:3px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`
export const NoGroups = styled.div`
  text-align:center; padding:32px 20px;
  color:${c.txt3}; font-size:12px; line-height:1.6;
`
export const UnassignedFooter = styled.div`
  padding:8px 12px; border-top:1px solid ${c.bdr}; margin-top:2px;
  font-size:11px; color:${c.txt3}; display:flex; justify-content:space-between;
  span:last-child { font-family:${c.mono}; }
`

// ─── GROUP CARD ───────────────────────────────────────────────────────────────
export const GCard = styled.div`
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:9px;
  margin-bottom:8px; overflow:hidden;
  border-left:3px solid ${({ $color }) => $color || c.txt3};
`
export const GCardHead = styled.div`
  display:flex; align-items:center; gap:8px; padding:9px 10px;
  cursor:pointer; user-select:none;
  &:hover { background:rgba(255,255,255,.02); }
`
export const GChevron = styled.span`
  font-size:10px; color:${c.txt3}; transition:transform .2s; flex-shrink:0;
  transform:${({ $open }) => $open ? 'rotate(90deg)' : 'none'};
`
export const GSwatch = styled.div`
  width:9px; height:9px; border-radius:50%;
  background:${({ $color }) => $color}; flex-shrink:0;
`
export const GName = styled.div`flex:1; font-size:12px; font-weight:600; color:${c.txt};`
export const GBtn = styled.button`
  font-size:11px; color:${c.txt3}; background:transparent; border:none;
  cursor:pointer; padding:3px 7px; border-radius:4px; line-height:1;
  &:hover { background:${c.bg4}; color:${({ $danger }) => $danger ? c.danger : c.txt}; }
`
export const GStats = styled.div`display:flex; gap:5px; padding:0 10px 9px; flex-wrap:wrap;`
export const GStat = styled.div`
  background:${c.bg4}; border-radius:5px; padding:4px 8px;
  font-size:10px; color:${c.txt2}; font-family:${c.mono};
  span { color:${c.txt}; font-weight:700; }
`
export const GOrders = styled.div`
  display:${({ $open }) => $open ? 'block' : 'none'};
  border-top:1px solid ${c.bdr}; max-height:240px; overflow-y:auto;
  &::-webkit-scrollbar { width:3px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`
export const GOrderRow = styled.div`
  display:flex; align-items:center; gap:6px;
  padding:6px 10px; border-bottom:1px solid ${c.bdr}; transition:.12s;
  &:last-child { border:none; }
  &:hover { background:${c.bg4}; }
`
export const GOrderNum = styled.div`
  font-family:${c.mono}; font-size:10px; color:${c.acc};
  flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
`
export const GOrderKg = styled.div`font-family:${c.mono}; font-size:10px; color:${c.warn}; flex-shrink:0;`
export const GOrderMove = styled.button`
  font-size:10px; background:${c.bg2}; border:1px solid ${c.bdr2}; color:${c.txt3};
  border-radius:5px; padding:2px 6px; cursor:pointer; flex-shrink:0; transition:.12s;
  &:hover { border-color:${c.acc}; color:${c.acc}; }
`
export const MoveDrop = styled.div`position:relative; display:inline-block;`
export const MoveMenu = styled.div`
  display:${({ $open }) => $open ? 'block' : 'none'};
  position:absolute; right:0; top:calc(100% + 4px);
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:8px;
  min-width:160px; z-index:800; box-shadow:0 8px 24px rgba(0,0,0,.6); overflow:hidden;
`
export const MoveMenuItem = styled.div`
  padding:7px 12px; font-size:11px; cursor:pointer; color:${c.txt2};
  white-space:nowrap; display:flex; align-items:center; gap:7px; transition:.1s;
  &:hover { background:${c.bg3}; color:${c.txt}; }
`
export const MoveMenuDivider = styled.div`height:1px; background:${c.bdr}; margin:3px 0;`

// ─── MAP CONTAINER ────────────────────────────────────────────────────────────
export const MapContainer = styled.div`flex:1; position:relative; height:100%; min-width:0;`
export const MapDiv = styled.div`position:absolute; top:0; right:0; bottom:0; left:0;`
export const BoxRect = styled.div`
  position:absolute; border:2px solid ${c.acc}; background:rgba(0,229,160,.08);
  pointer-events:none; z-index:900;
  display:${({ $visible }) => $visible ? 'block' : 'none'};
`
export const PolyHint = styled.div`
  display:${({ $visible }) => $visible ? 'block' : 'none'};
  position:absolute; top:52px; left:50%; transform:translateX(-50%);
  z-index:600; background:${c.bg2}; border:1px solid ${c.acc2}; border-radius:8px;
  padding:6px 14px; font-size:11px; color:${c.acc};
  pointer-events:none; white-space:nowrap; box-shadow:0 4px 16px rgba(0,0,0,.5);
`
export const MapCtrl = styled.div`
  position:absolute; top:10px; right:10px; z-index:500;
  display:flex; flex-direction:column; gap:5px;
`
export const CtrlBtn = styled.button`
  background:${c.bg2}; border:1px solid ${({ $active }) => $active ? c.warn : c.bdr2};
  border-radius:7px; color:${({ $active }) => $active ? c.warn : c.txt2};
  font-size:11px; padding:6px 11px; cursor:pointer; transition:.15s; white-space:nowrap;
  &:hover { border-color:${c.acc}; color:${c.acc}; }
`
export const MapStatus = styled.div`
  position:absolute; bottom:16px; right:10px; z-index:500;
  background:${c.bg2}; border:1px solid ${c.bdr}; border-radius:7px;
  padding:6px 11px; font-size:10px; color:${c.txt3}; font-family:${c.mono};
`
export const NewBtn = styled.button`
  position:absolute; top:10px; left:10px; z-index:500;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:7px;
  color:${c.txt3}; font-size:11px; padding:6px 11px; cursor:pointer; transition:.15s;
  &:hover { border-color:${c.warn}; color:${c.warn}; }
`

// ─── SELECTION BAR ────────────────────────────────────────────────────────────
export const SelBar = styled.div`
  position:absolute; bottom:16px; left:50%; transform:translateX(-50%);
  z-index:600; background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:10px 16px;
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; gap:14px;
  box-shadow:0 8px 32px rgba(0,0,0,.7); min-width:480px; max-width:calc(100% - 40px);
  animation:${slideUp} .2s ease;
`
export const SelInfo = styled.div`flex:1;`
export const SelCount = styled.div`font-family:${c.mono}; font-size:13px; font-weight:700; color:${c.acc};`
export const SelWeight = styled.div`
  font-size:11px; color:${c.txt2}; margin-top:2px;
  strong { color:${c.txt}; font-family:${c.mono}; }
`
export const SelReassignInfo = styled.div`
  font-size:10px; color:${c.warn}; margin-top:2px;
  display:${({ $visible }) => $visible ? 'block' : 'none'};
`
export const SelActions = styled.div`display:flex; gap:6px; flex-wrap:wrap; flex-shrink:0;`
export const SelClear = styled.button`
  background:transparent; border:1px solid ${c.bdr2}; color:${c.txt2};
  padding:6px 12px; border-radius:7px; cursor:pointer; font-size:11px; transition:.15s;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
`
export const SelCreate = styled.button`
  background:${c.acc}; border:none; color:#000;
  padding:6px 14px; border-radius:7px; cursor:pointer; font-size:11px; font-weight:700; transition:.15s;
  &:hover { background:${c.acc2}; }
`
export const SelAssignWrap = styled.div`position:relative;`
export const SelAssign = styled.button`
  background:${c.info}; border:none; color:#fff;
  padding:6px 14px; border-radius:7px; cursor:pointer; font-size:11px; font-weight:600; white-space:nowrap; transition:.15s;
  &:hover { filter:brightness(1.15); }
  &:disabled { opacity:.35; cursor:not-allowed; }
`
export const AssignMenu = styled.div`
  display:${({ $open }) => $open ? 'block' : 'none'};
  position:absolute; bottom:calc(100% + 8px); right:0;
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:10px;
  min-width:200px; z-index:800; box-shadow:0 8px 32px rgba(0,0,0,.7);
  overflow:hidden; max-height:260px; overflow-y:auto;
`
export const AssignMenuTitle = styled.div`
  padding:8px 12px 4px; font-size:9px; text-transform:uppercase; letter-spacing:.08em; color:${c.txt3};
`
export const AssignMenuItem = styled.div`
  padding:8px 12px; font-size:12px; cursor:pointer; color:${c.txt2};
  white-space:nowrap; display:flex; align-items:center; gap:8px; transition:.1s;
  &:hover { background:${c.bg3}; color:${c.txt}; }
  .g-cnt { font-family:${c.mono}; font-size:10px; color:${c.txt3}; margin-left:auto; }
`

// ─── PROGRESS SCREEN ─────────────────────────────────────────────────────────
export const ProgressScreen = styled.div`
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  flex:1; flex-direction:column; align-items:center; justify-content:center;
  background:${c.bg}; padding:40px 20px;
`
export const ProgBox = styled.div`
  max-width:420px; width:100%; text-align:center;
  .icon { font-size:44px; margin-bottom:14px; }
  h3 { font-size:15px; font-weight:600; margin-bottom:5px; color:${c.txt}; font-family:${c.sans}; }
  > p { font-size:13px; color:${c.txt2}; margin-bottom:22px; line-height:1.6; font-family:${c.sans}; }
`
export const ProgTrack = styled.div`height:4px; background:${c.bdr2}; border-radius:2px; overflow:hidden;`
export const ProgFill = styled.div`
  height:100%; background:${c.acc}; border-radius:2px; transition:width .2s;
  width:${({ $pct }) => $pct}%;
`
export const ProgMeta = styled.div`
  display:flex; justify-content:space-between; margin-top:7px;
  font-size:11px; color:${c.txt3}; font-family:${c.mono};
`
export const ProgCancel = styled.button`
  margin-top:18px; background:transparent; border:1px solid ${c.bdr2};
  color:${c.txt2}; padding:6px 16px; border-radius:6px; cursor:pointer; font-size:11px;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
`

// ─── ORDERS SCREEN ────────────────────────────────────────────────────────────
export const OrdersScreen = styled.div`
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  flex:1; flex-direction:column; background:${c.bg}; overflow:hidden;
`
export const OrdersToolbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:12px 20px; display:flex; gap:12px; align-items:center; flex-wrap:wrap; flex-shrink:0;
`
export const OrdersSearch = styled.input`
  flex:1; min-width:240px; max-width:380px;
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:8px;
  color:${c.txt}; padding:8px 12px; font-size:12px; font-family:${c.sans}; outline:none; transition:.15s;
  &:focus { border-color:${c.acc}; }
  &::placeholder { color:${c.txt3}; }
`
export const OrdersToolbarActions = styled.div`display:flex; gap:6px; margin-left:auto;`
export const OrdersToolBtn = styled.button`
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:7px;
  color:${c.txt2}; font-size:11px; padding:7px 12px; cursor:pointer; transition:.15s; font-family:${c.sans};
  &:hover { border-color:${c.acc}; color:${c.acc}; }
`
export const OrdersContent = styled.div`
  flex:1; overflow-y:auto; padding:16px 20px 40px;
  &::-webkit-scrollbar { width:6px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
export const StatusGroup = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr}; border-radius:10px;
  margin-bottom:12px; overflow:hidden; border-left:3px solid ${({ $color }) => $color || c.txt3};
`
export const StatusGroupHead = styled.div`
  display:flex; align-items:center; gap:10px; padding:12px 16px;
  cursor:pointer; user-select:none; transition:.15s;
  &:hover { background:${c.bg3}; }
`
export const StatusGroupChevron = styled.span`
  font-size:10px; color:${c.txt3}; transition:transform .2s; width:10px; flex-shrink:0;
  transform:${({ $open }) => $open ? 'rotate(90deg)' : 'none'};
`
export const StatusGroupName = styled.div`flex:1; font-size:13px; font-weight:600; color:${c.txt}; text-transform:capitalize;`
export const StatusGroupMetrics = styled.div`
  display:flex; gap:12px; font-family:${c.mono}; font-size:11px; color:${c.txt2}; flex-shrink:0;
  strong { color:${c.txt}; font-weight:700; }
`
export const StatusGroupBody = styled.div`
  display:${({ $open }) => $open ? 'block' : 'none'}; border-top:1px solid ${c.bdr};
`
export const OrdersTable = styled.table`
  width:100%; border-collapse:collapse; font-size:11px;
  thead { background:${c.bg3}; position:sticky; top:0; z-index:2; }
  th { text-align:left; padding:8px 10px; font-size:9px; font-weight:600; color:${c.txt3};
       text-transform:uppercase; letter-spacing:.06em; border-bottom:1px solid ${c.bdr}; white-space:nowrap; }
  td { padding:9px 10px; border-bottom:1px solid ${c.bdr}; color:${c.txt}; vertical-align:middle; }
  tbody tr { transition:.1s; cursor:pointer; &:hover { background:${c.bg3}; } &:last-child td { border-bottom:none; } }
`
export const ColPed = styled.td`font-family:${c.mono}; font-size:11px; font-weight:700; color:${c.acc}; white-space:nowrap;`
export const ColCidade = styled.td`color:${c.txt2}; white-space:nowrap;`
export const ColCep = styled.td`font-family:${c.mono}; font-size:10px; color:${c.txt3}; white-space:nowrap;`
export const ColDate = styled.td`font-family:${c.mono}; font-size:10px; color:${c.txt2}; white-space:nowrap;`
export const ColPeso = styled.td`font-family:${c.mono}; font-size:11px; color:${c.warn}; font-weight:600; text-align:right; white-space:nowrap;`
export const ColTransp = styled.td`font-size:10px; color:${c.txt3}; max-width:140px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;`
export const DriverPill = styled.div`
  display:inline-flex; align-items:center; gap:6px;
  padding:4px 10px; border-radius:20px; font-size:11px; font-weight:600;
  color:#fff; background:${({ $color }) => $color}; max-width:100%;
  .dot  { width:6px; height:6px; border-radius:50%; background:#fff; flex-shrink:0; opacity:.9; }
  .name { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
`
export const DriverNone = styled.span`
  display:inline-block; padding:4px 10px; border-radius:20px; font-size:10px;
  color:${c.txt3}; background:${c.bg3}; border:1px dashed ${c.bdr2}; font-style:italic;
`
export const OrdersEmpty = styled.div`
  text-align:center; padding:60px 20px; color:${c.txt3}; font-size:13px;
  .big { font-size:40px; margin-bottom:12px; opacity:.5; }
`

// ─── MODAL ────────────────────────────────────────────────────────────────────
export const ModalOverlay = styled.div`
  position:fixed; inset:0; background:rgba(0,0,0,.7); z-index:2000;
  display:${({ $open }) => $open ? 'flex' : 'none'};
  align-items:center; justify-content:center; backdrop-filter:blur(4px);
`
export const ModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  padding:24px; width:360px; box-shadow:0 20px 60px rgba(0,0,0,.8);
  h3 { font-size:15px; font-weight:700; margin-bottom:4px; color:${c.txt}; font-family:${c.sans}; }
  > p { font-size:12px; color:${c.txt2}; margin-bottom:18px; line-height:1.5; }
  label { font-size:11px; color:${c.txt3}; display:block; margin-bottom:5px;
          text-transform:uppercase; letter-spacing:.06em; }
  input { width:100%; background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:8px;
          color:${c.txt}; padding:9px 12px; font-size:13px; font-family:${c.sans}; outline:none; transition:.15s;
          &:focus { border-color:${c.acc}; } }
`
export const ModalSummary = styled.div`
  background:${c.bg3}; border-radius:8px; padding:10px 13px; margin:14px 0; display:flex; gap:16px;
`
export const MStat = styled.div`
  font-size:11px; color:${c.txt3};
  strong { display:block; font-family:${c.mono}; font-size:16px; color:${c.acc}; margin-bottom:1px; }
`
export const ModalActions = styled.div`display:flex; gap:8px; margin-top:18px;`
export const ModalCancel = styled.button`
  flex:1; background:transparent; border:1px solid ${c.bdr2}; color:${c.txt2};
  padding:9px; border-radius:8px; cursor:pointer; font-size:12px;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
`
export const ModalConfirm = styled.button`
  flex:2; background:${c.acc}; border:none; color:#000;
  padding:9px; border-radius:8px; cursor:pointer; font-size:12px; font-weight:700;
  &:hover { background:${c.acc2}; }
`
export const ModalEditActions = styled.div`
  display:${({ $visible }) => $visible ? 'block' : 'none'}; margin-top:12px;
  .label { font-size:10px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em; margin-bottom:6px; }
  .row   { display:flex; gap:6px; flex-wrap:wrap; }
`
export const BtnAddSel = styled.button`
  flex:1; padding:7px; background:${c.adim2}; border:1px solid ${c.acc2};
  border-radius:7px; color:${c.acc}; font-size:11px; font-weight:600; cursor:pointer;
  &:disabled { opacity:.35; cursor:not-allowed; }
`
export const BtnEmptySel = styled.button`
  padding:7px 12px; background:transparent; border:1px solid ${c.bdr2};
  border-radius:7px; color:${c.txt3}; font-size:11px; cursor:pointer;
`

// ─── ROUTE PANEL ──────────────────────────────────────────────────────────────
export const GRoutePanel = styled.div`
  border-top:1px solid ${c.bdr}; padding:10px;
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  flex-direction:column; gap:8px; background:${c.bg2};
`
export const RouteSpinner = styled.div`
  width:10px; height:10px; border:2px solid ${c.bdr2}; border-top-color:${c.acc};
  border-radius:50%; animation:${spin} .8s linear infinite; flex-shrink:0;
`
export const RouteInfoBar = styled.div`display:flex; gap:10px; align-items:center;`
export const RouteMetric = styled.div`
  flex:1; display:flex; flex-direction:column; min-width:0;
  .v { font-family:${c.mono}; font-size:13px; color:${c.acc}; font-weight:700; line-height:1; }
  .l { font-size:8px; color:${c.txt3}; margin-top:2px; text-transform:uppercase; letter-spacing:.06em; }
`
export const RouteWarn = styled.div`
  font-size:10px; color:${c.warn};
  background:rgba(245,158,11,.1); border:1px solid rgba(245,158,11,.3);
  border-radius:5px; padding:4px 7px;
`
export const RouteToggles = styled.div`display:flex; gap:4px;`
export const RouteToggle = styled.label`
  flex:1; min-width:0; display:flex; align-items:center; gap:5px;
  font-size:10px; padding:5px 7px; border-radius:5px;
  background:${c.bg3};
  border:1px solid ${({ $checked }) => $checked ? c.acc2 : c.bdr2};
  cursor:pointer; user-select:none;
  color:${({ $checked }) => $checked ? c.acc : c.txt2}; transition:.12s;
  input { margin:0; cursor:pointer; accent-color:${c.acc}; }
  &:hover { border-color:${c.acc2}; color:${c.txt}; }
`
export const RouteActions = styled.div`display:flex; gap:4px;`
export const RouteBtn = styled.button`
  flex:1; font-size:10px; padding:5px 6px;
  background:${({ $primary }) => $primary ? c.adim2 : c.bg3};
  border:1px solid ${({ $primary }) => $primary ? c.acc2 : c.bdr2};
  border-radius:5px;
  color:${({ $primary }) => $primary ? c.acc : c.txt2};
  cursor:pointer; transition:.12s; font-family:${c.sans}; white-space:nowrap;
  ${({ $primary }) => $primary && 'font-weight:600;'}
  &:hover { ${({ $primary }) => $primary ? `background:${c.acc};color:#000;` : `border-color:${c.acc};color:${c.acc};`} }
  &:disabled { opacity:.4; cursor:not-allowed; }
`
export const GRouteBtnStyled = styled.button`
  font-size:11px;
  background:${({ $active }) => $active ? c.acc : c.bg2};
  border:1px solid ${({ $active }) => $active ? c.acc : c.bdr2};
  color:${({ $active }) => $active ? '#000' : c.txt2};
  padding:3px 7px; border-radius:4px; cursor:pointer; line-height:1;
  &:hover { border-color:${c.acc}; }
`

// ─── STOPS EDITOR ─────────────────────────────────────────────────────────────
export const StopsEditor = styled.div`
  display:${({ $open }) => $open ? 'block' : 'none'};
  max-height:280px; overflow-y:auto;
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:6px; margin-top:4px;
  &::-webkit-scrollbar { width:3px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`
export const StopsEditorTitle = styled.div`
  padding:6px 10px; font-size:9px; text-transform:uppercase; letter-spacing:.08em;
  color:${c.txt3}; border-bottom:1px solid ${c.bdr}; background:${c.bg4};
  display:flex; align-items:center; justify-content:space-between;
  .hint { font-weight:400; text-transform:none; letter-spacing:0; }
`
export const RouteStopRow = styled.div`
  display:flex; align-items:center; gap:6px; padding:6px 8px;
  border-bottom:1px solid ${c.bdr}; background:${c.bg3}; transition:background .12s; position:relative;
  &:last-child { border-bottom:none; }
  &:hover { background:${c.bg4}; }
  &.dragging { opacity:.4; }
  &.drag-over-top::before {
    content:''; position:absolute; top:-1px; left:0; right:0;
    height:2px; background:${c.acc}; box-shadow:0 0 6px ${c.acc};
  }
  &.drag-over-bot::after {
    content:''; position:absolute; bottom:-1px; left:0; right:0;
    height:2px; background:${c.acc}; box-shadow:0 0 6px ${c.acc};
  }
`
export const StopDrag = styled.span`color:${c.txt3}; cursor:grab; font-size:12px; padding:0 2px; user-select:none; line-height:1; &:active { cursor:grabbing; }`
export const StopNum = styled.div`
  width:22px; height:22px; border-radius:50%; background:${c.bg2};
  border:2px solid ${({ $color }) => $color}; display:flex; align-items:center; justify-content:center;
  font-family:${c.mono}; font-size:10px; font-weight:700; color:${({ $color }) => $color};
  flex-shrink:0; line-height:1;
`
export const StopInfo = styled.div`flex:1; min-width:0; display:flex; flex-direction:column; gap:1px;`
export const StopPed = styled.div`font-family:${c.mono}; font-size:10px; color:${c.acc}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; line-height:1.2;`
export const StopCity = styled.div`font-size:10px; color:${c.txt3}; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; line-height:1.2;`
export const StopArrows = styled.div`display:flex; flex-direction:column; gap:2px; flex-shrink:0;`
export const StopArrow = styled.button`
  background:${c.bg2}; border:1px solid ${c.bdr2}; color:${c.txt3};
  font-size:9px; width:20px; height:14px; border-radius:3px; padding:0; cursor:pointer;
  line-height:1; display:flex; align-items:center; justify-content:center;
  &:hover:not(:disabled) { border-color:${c.acc}; color:${c.acc}; }
  &:disabled { opacity:.3; cursor:not-allowed; }
`
export const StopLinks = styled.div`
  display:flex; gap:3px; flex-shrink:0;
  a { background:${c.bg2}; border:1px solid ${c.bdr2}; color:${c.txt3};
      padding:3px 6px; border-radius:3px; text-decoration:none; font-size:9px;
      font-weight:600; transition:.12s; line-height:1;
      &:hover { border-color:${c.info}; color:${c.info}; }
      &.waze:hover { border-color:#33ccff; color:#33ccff; } }
`

// ─── AREAS SCREEN (estatísticas por zona) ───────────────────────────────────
export const AreasScreen = styled.div`
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  flex:1; flex-direction:column; background:${c.bg}; overflow:hidden;
`
export const AreasToolbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:14px 20px; flex-shrink:0;
`
export const AreasSummary = styled.div`
  display:flex; gap:18px; flex-wrap:wrap; align-items:center;
`
export const ASumStat = styled.div`
  font-size:11px; color:${c.txt3}; line-height:1.4;
  strong {
    display:inline-block; font-family:${c.mono}; font-size:18px; font-weight:700;
    color:${({ $warn }) => $warn ? c.warn : c.acc}; margin-right:5px;
  }
  .muted { color:${c.txt3}; font-family:${c.mono}; font-size:11px; }
`
export const AreasContent = styled.div`
  flex:1; overflow-y:auto; padding:18px 20px 40px;
  display:grid; grid-template-columns:repeat(auto-fill, minmax(360px, 1fr)); gap:14px;
  align-content:start;
  &::-webkit-scrollbar { width:6px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
export const AreasEmpty = styled.div`
  grid-column:1 / -1;
  text-align:center; padding:50px 20px; color:${c.txt3}; font-size:13px;
  background:${c.bg2}; border:1px dashed ${c.bdr2}; border-radius:10px;
  strong { color:${c.acc}; }
`
export const AreaStatCard = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr};
  border-left:3px solid ${({ $color }) => $color || c.txt3};
  border-radius:10px; padding:14px 16px;
  opacity:${({ $muted }) => $muted ? 0.7 : 1};
  transition:.15s;
  &:hover { border-color:${c.bdr2}; transform:translateY(-1px); }
`
export const AreaStatHead = styled.div`
  display:flex; align-items:center; gap:8px; margin-bottom:12px;
`
export const AreaStatSwatch = styled.div`
  width:10px; height:10px; border-radius:2px;
  background:${({ $color }) => $color}; flex-shrink:0;
`
export const AreaStatName = styled.div`
  flex:1; font-size:13px; font-weight:600; color:${c.txt};
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
`
export const AreaStatCarrier = styled.div`
  font-size:10px; color:${c.txt3}; font-family:${c.sans};
  background:${c.bg3}; padding:3px 8px; border-radius:5px;
  white-space:nowrap;
`
export const AreaStatRow = styled.div`
  display:grid; grid-template-columns:repeat(3, 1fr); gap:10px; margin-bottom:10px;
`
export const AreaStatMetric = styled.div`
  display:flex; flex-direction:column; gap:2px;
  .lbl { font-size:9px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.06em; }
  .val { font-family:${c.mono}; font-size:16px; font-weight:700; color:${c.txt}; line-height:1.1;
         em { font-style:normal; font-size:10px; color:${c.txt3}; font-weight:400; margin-left:2px; } }
  .muted { font-size:10px; color:${c.txt3}; font-family:${c.mono}; }
`
export const AreaStatBar = styled.div`
  height:4px; background:${c.bg3}; border-radius:2px; overflow:hidden;
`
export const AreaStatBarFill = styled.div`
  height:100%; background:${({ $color }) => $color}; border-radius:2px;
  width:${({ $pct }) => Math.min(100, Number($pct) || 0)}%;
  transition:width .25s ease;
`
